import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { applyAuthenticatedSession } from 'lib/auth';
import { Alert, Button } from 'components/common';
import { Form, FormGroup, Text } from 'components/common/form';

interface Props {
  next?: string;
  onCancel?: () => void;
  onSuccess?: () => void;
}

const LoginTwoFactor = ({ next, onCancel, onSuccess }: Props) => {
  const { dispatch } = useDispatch();

  const { messages, theme } = useReduxState();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);

  const handleBackupToggle = () => {
    setError(null);
    setUseBackupCode(!useBackupCode);
  };

  const handleSubmit = async (values) => {
    try {
      setError(null);
      setLoading(true);

      const response = await api.post('/auth/totp/authenticate', {
        useBackupCode,
        code: values.code
      });

      if (response.data?.success) {
        await applyAuthenticatedSession({
          dispatch,
          user: response.data.user
        });
        if (onSuccess) onSuccess();
        else window.location.href = next || '/';
      } else if (response?.data?.error === 'invalid_code') {
        setError(messages.authTwoFactorInvalidCode);
      } else if (response?.data?.error === 'too_many_failed_logins') {
        setError(
          `${messages.authTooManyFailedLoginsStart} ` +
            `${response.data.lockTemporarilyInMinutes} ` +
            messages.authTooManyFailedLoginsEnd
        );
      } else if (response?.data?.error === 'locked_after_failed_logins') {
        onCancel();
      } else {
        throw new Error('error');
      }
    } catch (err) {
      setError(messages.commonError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="two-factor">
      <Form onSubmit={handleSubmit}>
        <div className="info">
          {useBackupCode
            ? messages.authTwoFactorLoginBackupInfo
            : messages.authTwoFactorLoginInfo}
        </div>

        <FormGroup
          htmlFor="login-verification-code"
          label={
            useBackupCode
              ? messages.authEightDigitCode
              : messages.authSixDigitCode
          }
        >
          <Text
            autoComplete="one-time-code"
            field="code"
            id="login-verification-code"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder={messages.commonEnterCode}
            required={true}
            validateOnChange={true}
          />
        </FormGroup>

        {error ? (
          <div className="error">
            <Alert className="login-error" text={error} type="error-light" />
          </div>
        ) : null}

        <div className="submit">
          <Button
            category="success"
            loading={loading}
            style={{ width: '100%' }}
            text={messages.authVerifyCode}
            type="submit"
          />
        </div>

        <div className="footer">
          {!useBackupCode ? (
            <div className="footer-text">{messages.authTwoFactorNoApp}</div>
          ) : null}

          <button
            className="footer-button"
            onClick={handleBackupToggle}
            type="button"
          >
            {useBackupCode
              ? messages.authTwoFactorUserAuthenticatorApp
              : messages.authTwoFactorEnterBackupCode}
          </button>
        </div>
      </Form>

      <style jsx>{`
        .info {
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          font-weight: 400;
        }
        .error {
          margin-top: 20px;
        }
        .submit {
          margin-top: 20px;
        }
        .footer {
          color: ${theme.textColorLight};
          margin-top: 20px;
          font-size: 1.4rem;
          text-align: center;
        }
        .footer-text {
          margin-bottom: 5px;
        }
        .footer-button {
          background-color: transparent;
          border: 0;
          color: ${theme.linkColor};
          cursor: pointer;
          font-family: inherit;
          font-weight: 600;
          padding: 0;
          text-decoration: none;
        }
        .footer-button:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default LoginTwoFactor;
